import React, { useEffect, useState } from 'react';
import './MercadoPago.css';
import baseURL from '../url'; // Asegúrate de que `baseURL` esté definido correctamente

export default function MercadoPago({ items = [], payerEmail = '', order = {}, discountInfo = null }) {
    const [preferenceId, setPreferenceId] = useState(null);
    const [publicKey, setPublicKey] = useState('');
    const [isButtonInitialized, setIsButtonInitialized] = useState(false);
    const [initPoint, setInitPoint] = useState('');

    // Función para cargar el script de Mercado Pago dinámicamente solo si no está cargado
    const loadMercadoPagoScript = () => {
        if (!document.querySelector('script[src="https://sdk.mercadopago.com/js/v2"]')) {
            const script = document.createElement("script");
            script.src = "https://sdk.mercadopago.com/js/v2";
            script.async = true;
            script.onload = () => handleMercadoPago(); // Llamamos a la función que inicializa Mercado Pago
            document.body.appendChild(script);
        } else {
            handleMercadoPago(); // Si ya está cargado, inicializamos Mercado Pago directamente
        }
    };

    // Función para inicializar Mercado Pago una vez que el script esté cargado
    const handleMercadoPago = () => {
        if (!isButtonInitialized && preferenceId) {
            const key = publicKey || 'TEST-018adba1-4218-4371-98b6-8dcec99816f6';
            const mp = new window.MercadoPago(key, { locale: 'es-CO' });
            const bricksBuilder = mp.bricks();
            try {
                bricksBuilder.create('wallet', 'wallet_container', {
                    initialization: { preferenceId },
                    customization: { texts: { valueProp: 'smart_option' } }
                });
                setIsButtonInitialized(true);
            } catch(e){ console.error('Error creando Wallet Brick', e); }
        }
    };

    // Obtener configuración (Public Key) desde backend
    useEffect(() => {
        const getConfig = async () => {
            try {
                const r = await fetch(`${baseURL}/mpConfigGet.php`);
                const t = await r.text();
                try {
                    const d = JSON.parse(t);
                    setPublicKey(d.publicKey || '');
                } catch(_){ console.error('Config MP no es JSON', t); }
            } catch(e){ console.error('Error al cargar config MP', e); }
        };
        getConfig();
    }, []);

    // Crear preferencia de pago vía backend
    useEffect(() => {
        const createPreference = async () => {
            try {
                const baseItems = (items || []).map(it => ({
                    title: it?.titulo || it?.title || 'Producto',
                    quantity: Number(it?.cantidad || it?.quantity || 1),
                    unit_price: Number(it?.precio || it?.unit_price || 0),
                }));
                let adjItems = baseItems;
                try {
                    const total = baseItems.reduce((acc, it)=> acc + (it.unit_price * it.quantity), 0);
                    if (discountInfo && discountInfo.aplicable && total > 0) {
                        const amount = Number(discountInfo.amount || 0);
                        const percent = discountInfo.percent;
                        if (percent) {
                            const factor = Math.max(0, (100 - Number(percent)) / 100);
                            adjItems = baseItems.map(it => ({ ...it, unit_price: Number((it.unit_price * factor).toFixed(2)) }));
                        } else {
                            // Distribuir descuento fijo proporcionalmente
                            adjItems = baseItems.map(it => {
                                const share = (it.unit_price * it.quantity) / total;
                                const reduce = share * amount / it.quantity; // descuento por unidad
                                const newPrice = Math.max(0, it.unit_price - reduce);
                                return { ...it, unit_price: Number(newPrice.toFixed(2)) };
                            });
                        }
                    }
                } catch(_){ adjItems = baseItems; }
                const payload = {
                    items: adjItems,
                    payer: { email: payerEmail || 'compras@example.com' },
                    order,
                };
                const response = await fetch(`${baseURL}/mpPreferencePost.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept':'application/json' },
                    body: JSON.stringify(payload),
                });
                const text = await response.text();
                try {
                    const preference = JSON.parse(text);
                    if (preference?.id) {
                        setPreferenceId(preference.id);
                        if (preference?.init_point) setInitPoint(preference.init_point);
                    }
                    else console.error('Preferencia MP error', preference);
                } catch(err){
                    console.error('Error al crear la preferencia:', err, text);
                }
            } catch (error) {
                console.error('Error al crear la preferencia:', error);
            }
        };
        if ((items || []).length > 0) createPreference();
    }, [items, payerEmail]);

    // Cargar el script de Mercado Pago después de crear la preferencia
    useEffect(() => {
        if (preferenceId) {
            loadMercadoPagoScript();
        }
    }, [preferenceId, publicKey]);

    return (
        <div>
            {preferenceId ? (
                <div className='btnMp'>
                    <div id="wallet_container"></div>
                    <div style={{ marginTop:12, width:'100%', maxWidth:420 }}>
                        <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:10 }}>
                            <div style={{ fontWeight:700, marginBottom:6 }}>Resumen</div>
                            <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                                {(items||[]).map((it,idx)=> (
                                    <li key={idx} style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:'#374151' }}>
                                        <span>{it?.titulo||it?.title||'Producto'} x {Number(it?.cantidad||it?.quantity||1)}</span>
                                        <span>${Number(it?.precio||it?.unit_price||0).toLocaleString('es-ES')}</span>
                                    </li>
                                ))}
                            </ul>
                            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8, fontWeight:800 }}>
                                <span>Total</span>
                                <span>${(items||[]).reduce((acc, it)=> acc + Number(it?.precio||it?.unit_price||0) * Number(it?.cantidad||it?.quantity||1), 0).toLocaleString('es-ES')}</span>
                            </div>
                        </div>
                        {initPoint ? (
                            <div style={{ marginTop:10, textAlign:'center' }}>
                                <a href={initPoint} target="_blank" rel="noopener noreferrer" className='btn' style={{ display:'inline-block', padding:'10px 14px', marginTop:6 }}>Pagar en Mercado Pago (externo)</a>
                                <div style={{ color:'#6b7280', fontSize:12, marginTop:4 }}>Si el botón embebido no se habilita, usa el pago externo.</div>
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : (<p>Cargando...</p>)}
        </div>
    );
}

//produccion
// APP_USR-1771609002540620-102217-936885c13884fbef9892aeb7bad2bb1b-274113222

// APP_USR-a22d882c-c5a9-43d5-91bd-4f97bcf1cd3f

//prueba test
//TEST-1771609002540620-102217-2f8e7b82aab4d3d590d289e6caa73b0c-274113222
//TEST-018adba1-4218-4371-98b6-8dcec99816f6
